import * as XLSX from "xlsx";

import type { BenchmarkResultPayloadType } from "@/types";

const competenciesPerGroup = 5;

const headerRowOffset = 5;

interface BuildBenchmarkXlsxInputInterface {
    includeMetaRows?: boolean,
    industry?: string,
    jobTitle?: string,
    result: BenchmarkResultPayloadType,
    seniority?: string,
}

const buildAoaHandler = ({
    includeMetaRows = true,
    industry,
    jobTitle,
    result,
    seniority,
}: BuildBenchmarkXlsxInputInterface): (number | string | null)[][] => {
    const rows: (number | string | null)[][] = [];

    if (includeMetaRows) {
        rows.push([]);

        rows.push([
            "job_title",
            jobTitle ?? result.jobTitle ?? "",
            null,
            "industry",
            industry ?? result.industry ?? "",
        ]);

        rows.push([
            "seniority",
            seniority ?? result.seniority ?? "",
        ]);

        rows.push([]);
    }

    rows.push([
        "Group Title",
        "Question No.",
        "Value",
        "Text",
    ]);

    let questionCounter = 1;

    const branches = result.selectedBranches ?? [];

    branches.forEach((branch) => {
        for (let competencyIndex = 0; competencyIndex < competenciesPerGroup; competencyIndex += 1) {
            const value = branch.chosenCompetencies[competencyIndex] ?? 0;

            const text = branch.chosenCompetenciesText[competencyIndex] ?? "";

            rows.push([
                competencyIndex === 0 ? branch.jobType : "",
                questionCounter,
                value,
                text,
            ]);

            questionCounter += 1;
        }
    });

    const environment = result.environmentStatus ?? [];

    environment.forEach((entry, index) => {
        rows.push([
            index === 0 ? "environment_status" : "",
            questionCounter,
            entry.selectedOption,
            entry.answerText,
        ]);

        questionCounter += 1;
    });

    return rows;
};

export const buildBenchmarkWorkbookHandler = (input: BuildBenchmarkXlsxInputInterface): XLSX.WorkBook => {
    const aoa = buildAoaHandler(input);

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);

    worksheet["!cols"] = [
        { wch: 22 },
        { wch: 12 },
        { wch: 22 },
        { wch: 24 },
        { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Benchmark",
    );

    return workbook;
};

export const downloadBenchmarkXlsxHandler = (input: BuildBenchmarkXlsxInputInterface, fileName: string): void => {
    const workbook = buildBenchmarkWorkbookHandler(input);

    XLSX.writeFile(
        workbook,
        fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`,
    );
};

export const renderBenchmarkXlsxHtmlHandler = (input: BuildBenchmarkXlsxInputInterface): string => {
    const workbook = buildBenchmarkWorkbookHandler({
        ...input,
        includeMetaRows: false,
    });

    const sheetName = workbook.SheetNames[0];

    return XLSX.utils.sheet_to_html(workbook.Sheets[sheetName]);
};

export const benchmarkHeaderRowOffset = headerRowOffset;
